import {
  CLEAR_FILTER,
  REMOVE_TABLE_FILTER,
  CLEAR_LIMITATION,
  CLEAR_PAGINATION_LIMIT,
  CLEAR_TABLE_FILTER,
  makeQueryParams,
  makeSearchParams,
  PAGINATION,
  REMOVE_FILTER,
  SEARCH,
  SET_BUFFER_LINES,
  SET_CURRENT_PAGE,
  SET_FILE,
  SET_FILTER,
  SET_FILTER_DATA,
  SET_FILTER_GRAPH,
  SET_QUERY,
  SET_QUERY_FILTER,
  SET_TABLE_FILTER,
  SET_TYPE,
  UPDATE_LIMITATION
} from '../actions/search'
import {SET_PRODUCTS, SET_PRODUCTS_LOADING, SET_SELECTED} from '../actions/product'
import {CLEAR_FOUND_AXIOS_SOURCE, SET_FOUND_DOM} from '../actions/found'
import {DepGraph} from 'dependency-graph'
import get from 'lodash/get'
import has from 'lodash/has'

const REFERENCES_SEPARATOR = ';'
const FILTER_TYPES = ['rate']
const EMPTY_TABLE_FILTER = {
  reference: '',
  description: '',
  rate: null,
  lines: null
}

const state = {
  query: '',
  tableFilter: { ...EMPTY_TABLE_FILTER },
  bufferLines: {},
  file: null,
  pagination: {
    limit: null,
    current: 1,
    perPage: 0,
    pageCount: 0,
    totalCount: 0
  },
  type: null,
  isLoaded: {
    common: false,
    rate: false,
    static: false,
    table: false
  },
  data: {
    common: {
      brand: {},
      business: {},
      subcategory: {},
      category: {},
      range: {}
    },
    static: {
      card: {},
      marketing: {},
      logistic: {}
    },
    rate: {
      brand: {},
      activity: {},
      collection: {},
      line: {},
      subline: {}
    },
    table: {
      rate: {},
      lines: {}
    }
  },
  filters: {
    common: {
      brand: [],
      business: [],
      subcategory: [],
      category: [],
      range: []
    },
    static: {
      card: [],
      marketing: [],
      logistic: []
    },
    rate: {
      brand: [],
      activity: [],
      collection: [],
      line: [],
      subline: []
    },
    table: {
      rate: [],
      lines: []
    }
  },
  graph: {
    common: new DepGraph(),
    rate: new DepGraph()
  },
  limitation: {
    common: {
      brand: false,
      business: false,
      subcategory: false,
      category: false,
      range: false
    },
    rate: {
      brand: false,
      activity: false,
      collection: false,
      line: false,
      subline: false
    }
  }
}

const getters = {
  isEmptyData: state => (type) => !has(state.isLoaded, type) || !state.isLoaded[type],
  getData: state => (type, filter) => get(state.data, [type, filter], {}),
  getDataTitle: state => (type, filter, id) => get(state.data, [type, filter, id, 'title'], null),
  getFilter: state => (type, filter) => get(state.filters, [type, filter], []),
  getFilters: state => (type) => get(state.filters, type, {}),
  getFiltersLength: state => (type) => {
    let length = 0
    const filter = get(state.filters, type, {})
    Object.values(filter).forEach((items) => {
      length += items.length
    })
    return length
  },
  getLimitation: state => (type, filter) => get(state.limitation, [type, filter], false),
  getQuery: state => state.query,
  getSplitQuery: state => state.query.split(REFERENCES_SEPARATOR),
  getType: state => state.type,
  getPagination: state => state.pagination,
  getFile: state => state.file,
  getTableFilter: state => state.tableFilter,
  getBufferLines: state => state.bufferLines,
  isEmptyTableFilters: state => Object.values(state.tableFilter).every(value => !value),
  hasAnyFilter: (state, getters) => getters.filtersLength ||
    !getters.isEmptyTableFilters ||
    getters.getProducts.length ||
    !!getters.getQuery ||
    !!getters.getFile,
  getSearchParams: (state, getters) => {
    const params = makeSearchParams(state, getters)
    if (Object.prototype.hasOwnProperty.call(params, 'lines')) {
      params.line_id = params.lines
      delete params.lines
    }
    return params
  }
}

function commitSetFilter (commit, { type, filter, value }) {
  if (type !== 'static') {
    value = parseInt(value)
    if (value && !isNaN(value)) {
      commit(SET_FILTER, { type, filter, value })
    }
  } else {
    commit(SET_FILTER, { type, filter, value })
  }
}

function commitQueryFilter ({ commit, getters }, type, query) {
  const filters = getters.getFilters(type)
  Object.keys(filters).forEach((filter) => {
    if (Object.prototype.hasOwnProperty.call(query, filter)) {
      if (Array.isArray(query[filter])) {
        query[filter].forEach((value) => {
          commitSetFilter(commit, { type, filter, value })
        })
      } else {
        commitSetFilter(commit, { type, filter, value: query[filter] })
      }
    }
  })
}

const actions = {
  [SET_FILTER_DATA] ({ commit }, { type, data }) {
    return new Promise((resolve) => {
      const { relation } = data
      commit(SET_FILTER_DATA, { type, data })
      commit(SET_FILTER_GRAPH, { type, relation: relation || [] })
      resolve()
    })
  },
  [SET_FILTER]: ({ commit }, { type, filter, value }) => {
    return new Promise((resolve) => {
      commit(SET_FILTER, { type, filter, value })
      commit(CLEAR_LIMITATION)
      commit(UPDATE_LIMITATION, type)
      resolve()
    })
  },
  [REMOVE_FILTER]: ({ commit }, { type, filter }) => {
    return new Promise((resolve) => {
      commit(REMOVE_FILTER, { type, filter })
      resolve()
    })
  },
  [REMOVE_TABLE_FILTER]: ({ commit }, { filter }) => {
    return new Promise((resolve) => {
      commit(REMOVE_TABLE_FILTER, { filter })
      resolve()
    })
  },
  [CLEAR_FILTER]: ({ commit }, { router }) => {
    return new Promise((resolve) => {
      commit(CLEAR_FILTER)
      commit(CLEAR_LIMITATION)
      commit(SET_PRODUCTS, [])
      commit(CLEAR_TABLE_FILTER)
      commit(SET_QUERY, '')
      commit(SET_FILE, null)
      commit(SET_SELECTED, [])
      if (router) {
        router.push({ path: '' }).catch(()=>{})
      }
      resolve()
    })
  },
  [SET_QUERY]: ({ commit }, query) => {
    return new Promise((resolve) => {
      commit(SET_QUERY, query)
      resolve()
    })
  },
  [SET_BUFFER_LINES]: ({ commit }, buffer) => {
    return new Promise((resolve) => {
      commit(SET_BUFFER_LINES, buffer)
      resolve()
    })
  },
  [SET_QUERY_FILTER]: ({ commit, getters }, query) => {
    return new Promise((resolve) => {
      if (query.references) {
        commit(
          SET_QUERY, 
          Array.isArray(query.references)
            ? query.references.join(REFERENCES_SEPARATOR)
            : query.references
        )
      }
      const page = parseInt(query.page)
      commit(SET_CURRENT_PAGE, page && !isNaN(page) ? page : 1)
      const type = getters.getType
      commitQueryFilter ({ commit, getters }, type, query)
      commitQueryFilter ({ commit, getters }, 'static', query)
      commit(UPDATE_LIMITATION, getters.getType)
      Object.keys(EMPTY_TABLE_FILTER).forEach((filter) => {
        if (Object.prototype.hasOwnProperty.call(query, filter)) {
          const value = Array.isArray(query[filter])
            ? query[filter][0]
            : query[filter]

          commit(SET_TABLE_FILTER, { filter, value })
          const type = 'table'
          commit(SET_FILTER, { type, filter, value })
        }
      })
      resolve()
    })
  },
  [SEARCH] ({ commit, dispatch, getters, state }, data) {
    return new Promise((resolve, reject) => {
      commit(SET_PRODUCTS_LOADING, true)
      if (!data || !Object.prototype.hasOwnProperty.call(data, 'reset')) {
        if (!data) data = {}
        data.reset = true
      }
      dispatch(CLEAR_FOUND_AXIOS_SOURCE)
      if (data.reset) {
        commit(SET_CURRENT_PAGE, 1)
        commit(SET_SELECTED, [])
        commit(SET_FOUND_DOM, null)
      }
      const params = makeSearchParams(state, getters)
      if (data.router) {
        data.router.push({ path: '', query: makeQueryParams(params, state) }).catch(()=>{})
      }
      if (Object.prototype.hasOwnProperty.call(params, 'lines')) {
        params.line_id = params.lines
        delete params.lines
      }
      this._vm.$api.catalog.search(params, { params: { page: state.pagination.current }}).then((response) => {
        commit(SET_PRODUCTS, response.data)
        commit(PAGINATION, {
          current: parseInt(response.headers['x-pagination-current-page']),
          perPage: parseInt(response.headers['x-pagination-per-page']),
          pageCount: parseInt(response.headers['x-pagination-page-count']),
          totalCount: parseInt(response.headers['x-pagination-total-count']),
          ...{ limit: response.headers['x-pagination-limit'] ? parseInt(response.headers['x-pagination-limit']) : null }
        })
        commit(SET_PRODUCTS_LOADING, false)
        resolve(response)
      }).catch(err => {
        reject(err)
      })
    })
  },
  [SET_CURRENT_PAGE]: ({ commit, dispatch }, data) => {
    return new Promise((resolve) => {
      const { router, page } = data
      commit(SET_CURRENT_PAGE, page)
      dispatch(SEARCH, { router, reset: false })
      resolve()
    })
  },
  [SET_TYPE]: ({ commit }, type) => {
    return new Promise((resolve, reject) => {
      if (FILTER_TYPES.includes(type)) {
        commit(SET_TYPE, type)
        resolve()
      } else {
        reject()
      }
    })
  },
  [SET_FILE]: ({ commit }, file) => {
    return new Promise((resolve) => {
      commit(SET_FILE, file)
      if (file !== null) {
        commit(SET_QUERY, '')
      }
      resolve()
    })
  },
  [CLEAR_PAGINATION_LIMIT]: ({ commit }) => {
    return new Promise((resolve) => {
      commit(CLEAR_PAGINATION_LIMIT)
      resolve()
    })
  },
  [SET_TABLE_FILTER]: ({ commit, state }, { filter, value }) => {
    return new Promise((resolve) => {
      if (has(state.tableFilter, filter)) {
        commit(SET_TABLE_FILTER, { filter, value })
      }
      resolve()
    })
  }
}

const mutations = {
  [SET_FILTER_DATA]: (state, { type, data }) => {
    if (Object.prototype.hasOwnProperty.call(state.isLoaded, type) && Object.prototype.hasOwnProperty.call(state.data, type)) {
      state.isLoaded[type] = true
      state.data[type] = data
    }
  },
  [SET_FILTER_GRAPH]: (state, { type, relation }) => {
    if (!Object.prototype.hasOwnProperty.call(state.graph, type) || !(state.graph[type] instanceof DepGraph)) return
    relation.forEach((item) => {
      const items = Object.entries(item)
      for (let index = 0; index < items.length; index++) {
        let [selfLevel, selfID] = items[index]
        selfID = parseInt(selfID)
        if (!state.graph[type].hasNode([selfLevel, selfID])) {
          state.graph[type].addNode([selfLevel, selfID])
        }
        if (index > 0) {
          let [beforeLevel, beforeID] = items[index - 1]
          beforeID = parseInt(beforeID)
          state.graph[type].addDependency(
            [beforeLevel, beforeID],
            [selfLevel, selfID]
          )
        }
      }
    })
  },
  [SET_FILTER]: (state, { type, filter, value }) => {
    if (
      !Object.prototype.hasOwnProperty.call(state.filters, type) ||
      !Object.prototype.hasOwnProperty.call(state.filters[type], filter) ||
      !Object.prototype.hasOwnProperty.call(state.data, type) ||
      !Object.prototype.hasOwnProperty.call(state.data[type], filter) ||
      (value && !Object.prototype.hasOwnProperty.call(state.data[type][filter], value))
    ) {
      return
    }
    if (value === null) {
      state.filters[type][filter] = []
    } else {
      const index = state.filters[type][filter].findIndex((item) => item === value)
      if (index !== -1) {
        state.filters[type][filter].splice(index, 1)
      } else {
        state.filters[type][filter].push(value)
      }
    }
  },
  [REMOVE_FILTER]: (state, { type, filter }) => {
    if (
        !Object.prototype.hasOwnProperty.call(state.filters, type) ||
        !Object.prototype.hasOwnProperty.call(state.filters[type], filter)
    ) {
      return
    }
    state.filters[type][filter] = []
  },
  [CLEAR_FILTER]: (state) => {
    Object.entries(state.filters).forEach(([type, filters]) => {
      Object.entries(filters).forEach(([filter]) => {
        state.filters[type][filter] = []
      })
    })
  },
  [UPDATE_LIMITATION]: (state, type) => {
    if (!has(state.filters, type) || !has(state.limitation, type) || !has(state.graph, type)) return
    let limitation = []
    const filtred = []
    const notFiltred = {}
    Object.entries(state.filters[type]).forEach(([limit, values]) => {
      if (limit === 'brand' && values.length) {
        limitation = []
        return;
      }
      if (values.length) {
        filtred.push(limit)
      } else {
        notFiltred[limit] = []
      }
      values.forEach((value) => {
        try {
          limitation = limitation.concat(state.graph[type].dependenciesOf([limit, value]))
          limitation = limitation.concat(state.graph[type].dependantsOf([limit, value]))
        } catch (e) {
          /* empty */
        }
      })
    })
    limitation = limitation.reduce((allLimits, [limit, value]) => {
      if (!allLimits[limit]) {
        allLimits[limit] = [value]
      } else {
        allLimits[limit].push(value)
      }
      return allLimits
    }, {})
    if (!Object.keys(limitation).length && filtred.length) {
      limitation = {
        ...limitation,
        ...notFiltred
      }
    }
    state.limitation[type] = {
      ...state.limitation[type],
      ...limitation
    }
  },
  [CLEAR_LIMITATION]: (state) => {
    Object.entries(state.limitation).forEach(([type, limits]) => {
      Object.entries(limits).forEach(([limit]) => {
        state.limitation[type][limit] = false
      })
    })
  },
  [SET_QUERY]: (state, query) => {
    state.query = query
  },
  [PAGINATION]: (state, pagination) => {
    state.pagination = pagination
  },
  [SET_CURRENT_PAGE]: (state, page) => {
    state.pagination.current = page
  },
  [SET_TYPE]: (state, type) => {
    state.type = type
  },
  [SET_FILE]: (state, file) => {
    state.file = file
  },
  [CLEAR_PAGINATION_LIMIT]: (state) => {
    state.pagination.limit = null
  },
  [SET_TABLE_FILTER]: (state, { filter, value }) => {
    state.tableFilter[filter] = value
  },
  [CLEAR_TABLE_FILTER]: (state) => {
    state.tableFilter = { ...EMPTY_TABLE_FILTER }
  },
  [REMOVE_TABLE_FILTER]: (state, { filter }) => {
    state.tableFilter[filter] = null
  },
  [SET_BUFFER_LINES]: (state, buffer) => {
    state.bufferLines = buffer
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
